import React from "react";
import { Link } from "react-router-dom";
import {
  MdAdd,
  MdInbox,
  MdMenuBook,
  MdPeople,
  MdSchool,
  MdSubject,
  MdTrendingUp,
} from "react-icons/md";
import { Skeleton } from "components/ui/Skeleton";
import { useAppLanguage } from "hooks/useAppLanguage";
import { formatLocaleDateTime } from "lib/localeFormat";
import { adminAuthService } from "services/adminAuthService";
import {
  dashboardService,
  type DashboardRecentCourse,
  type DashboardRecentEnrollment,
  type DashboardRecentInstructor,
  type DashboardRecentStudent,
  type DashboardSummary,
} from "services/dashboardService";
import {
  DashboardEmptyState,
  DashboardErrorState,
  DashboardSection,
  formatDashboardDate,
  KpiCard,
  KpiGrid,
} from "views/admin/default/components/DashboardUi";
import { UI_ACTIONS_GRID, UI_DASHBOARD_SPLIT, UI_DASHBOARD_STACK } from "lib/uiClasses";

function RecentList<T extends { id: number }>({
  loading,
  items,
  emptyLabel,
  renderItem,
}: {
  loading: boolean;
  items: T[];
  emptyLabel: string;
  renderItem: (item: T) => React.ReactNode;
}) {
  if (loading) {
    return (
      <div className="space-y-2">
        {Array.from({ length: 3 }).map((_, index) => (
          <Skeleton key={index} className="h-10 w-full rounded-lg" />
        ))}
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <DashboardEmptyState
        icon={<MdInbox className="h-4 w-4" />}
        title={emptyLabel}
      />
    );
  }

  return (
    <ul className="divide-y divide-gray-100 dark:divide-white/10">
      {items.map((item) => (
        <li key={item.id}>{renderItem(item)}</li>
      ))}
    </ul>
  );
}

const Dashboard = () => {
  const language = useAppLanguage();
  const isArabic = language === "ar";
  const locale = isArabic ? "ar" : "en";

  const [summary, setSummary] = React.useState<DashboardSummary | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [adminName, setAdminName] = React.useState<string | null>(null);
  const [reloadKey, setReloadKey] = React.useState(0);

  const nowLabel = React.useMemo(() => {
    return formatLocaleDateTime(new Date(), isArabic, {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  }, [isArabic]);

  React.useEffect(() => {
    let cancelled = false;

    adminAuthService
      .getProfile()
      .then((profile) => {
        if (!cancelled) {
          setAdminName(profile?.name || profile?.email || profile?.phone || null);
        }
      })
      .catch(() => {
        if (!cancelled) setAdminName(null);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  React.useEffect(() => {
    let cancelled = false;

    (async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await dashboardService.getSummary();
        if (!cancelled) setSummary(data);
      } catch (e: unknown) {
        if (cancelled) return;
        const message =
          (e as { response?: { data?: { message?: string } } })?.response?.data?.message ||
          (isArabic ? "تعذر تحميل لوحة التحكم" : "Failed to load dashboard");
        setError(message);
        setSummary(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isArabic, reloadKey]);

  const quickActions = [
    {
      label: isArabic ? "إضافة طالب" : "Add student",
      href: "/admin/students",
      icon: <MdPeople className="h-4 w-4" />,
    },
    {
      label: isArabic ? "إضافة مدرّس" : "Add instructor",
      href: "/admin/instructors",
      icon: <MdSchool className="h-4 w-4" />,
    },
    {
      label: isArabic ? "إضافة مادة" : "Add material",
      href: "/admin/academic",
      icon: <MdSubject className="h-4 w-4" />,
    },
  ];

  const recent = summary?.recent;

  return (
    <div className={UI_DASHBOARD_STACK} dir={isArabic ? "rtl" : "ltr"}>
      <header className="apple-dashboard-hero relative isolate overflow-hidden p-5 text-white sm:p-7">
        <div className="pointer-events-none absolute -right-12 -top-20 h-64 w-64 rounded-full bg-white/10 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-24 left-1/3 h-48 w-48 rounded-full bg-cyan-300/10 blur-3xl" />
        <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-start sm:justify-between sm:gap-4">
          <div className="min-w-0">
            <span className="mb-3 inline-flex items-center gap-2 rounded-full bg-white/15 px-3 py-1 text-[11px] font-bold text-white/90 backdrop-blur-sm">
              <MdSchool className="h-4 w-4" />
              {isArabic ? "مركز التحكم" : "Control Center"}
            </span>
            <h1 className="truncate text-2xl font-extrabold text-white sm:text-3xl">
              {isArabic ? "لوحة التحكم" : "Dashboard"}
            </h1>
            <p className="mt-1 text-sm text-emerald-50/80 sm:text-base">
              {isArabic ? "نظرة عامة على النظام" : "System overview"}
            </p>
            {adminName ? (
              <p className="mt-3 truncate text-sm font-medium text-white/90">
                {isArabic ? `مرحبًا، ${adminName}` : `Welcome, ${adminName}`}
              </p>
            ) : null}
          </div>
          <p className="text-xs leading-snug text-white/70 sm:text-sm sm:text-end">
            {nowLabel}
          </p>
        </div>
      </header>

      {error && !loading ? (
        <DashboardErrorState
          message={error}
          onRetry={() => setReloadKey((value) => value + 1)}
          retryLabel={isArabic ? "إعادة المحاولة" : "Retry"}
        />
      ) : null}

      <DashboardSection
        title={isArabic ? "مؤشرات عامة" : "Overview KPIs"}
        subtitle={isArabic ? "أعداد حقيقية من النظام" : "Live counts from the system"}
      >
        <KpiGrid>
          <KpiCard
            icon={<MdPeople className="h-6 w-6" />}
            label={isArabic ? "عدد الطلاب" : "Students"}
            value={summary?.students_count ?? 0}
            loading={loading}
            href="/admin/students"
          />
          <KpiCard
            icon={<MdSchool className="h-6 w-6" />}
            label={isArabic ? "عدد المدرسين" : "Instructors"}
            value={summary?.instructors_count ?? 0}
            loading={loading}
            href="/admin/instructors"
          />
          <KpiCard
            icon={<MdMenuBook className="h-6 w-6" />}
            label={isArabic ? "عدد الكورسات" : "Courses"}
            value={summary?.courses_count ?? 0}
            loading={loading}
            href="/admin/entities"
          />
          <KpiCard
            icon={<MdSubject className="h-6 w-6" />}
            label={isArabic ? "عدد المواد" : "Materials"}
            value={summary?.materials_count ?? 0}
            loading={loading}
            href="/admin/academic"
          />
        </KpiGrid>
      </DashboardSection>

      <div className={UI_DASHBOARD_SPLIT}>
        <DashboardSection
          title={isArabic ? "نظرة أكاديمية" : "Academic overview"}
          subtitle={isArabic ? "الهيكل الأكاديمي والمواد" : "Academic structure and materials"}
        >
          <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-3 sm:gap-3">
            {[
              {
                label: isArabic ? "المراحل" : "Stages",
                value: summary?.stages_count ?? 0,
              },
              {
                label: isArabic ? "الصفوف" : "Classes",
                value: summary?.classes_count ?? 0,
              },
            ].map((item) => (
              <div
                key={item.label}
                className="rounded-xl border border-gray-100 bg-gray-50/60 p-3 dark:border-white/5 dark:bg-white/[0.02]"
              >
                <p className="line-clamp-2 min-h-[2.25rem] text-[11px] text-gray-500 dark:text-gray-400 sm:text-xs">
                  {item.label}
                </p>
                {loading ? (
                  <Skeleton className="mt-2 h-6 w-12" />
                ) : (
                  <p className="mt-1 text-lg font-bold text-navy-700 dark:text-white sm:text-xl">
                    {item.value}
                  </p>
                )}
              </div>
            ))}
          </div>
        </DashboardSection>

        <DashboardSection
          title={isArabic ? "نظرة تعليمية" : "Learning overview"}
          subtitle={isArabic ? "الكورسات والتسجيلات والنشاط" : "Courses, enrollments, and activity"}
        >
          <div className="grid grid-cols-2 gap-2.5 sm:gap-3">
            {[
              {
                label: isArabic ? "إجمالي الكورسات" : "Total courses",
                value: summary?.courses_count ?? 0,
              },
              {
                label: isArabic ? "كورسات بها تسجيلات" : "Courses with enrollments",
                value: summary?.courses_with_enrollments_count ?? 0,
              },
              {
                label: isArabic ? "إجمالي التسجيلات" : "Total enrollments",
                value: summary?.enrollments_count ?? 0,
              },
              {
                label: isArabic ? "الطلاب النشطون" : "Active students",
                value: summary?.active_students_count ?? 0,
              },
              {
                label: isArabic ? "المدرسون النشطون" : "Active instructors",
                value: summary?.active_instructors_count ?? 0,
              },
            ].map((item) => (
              <div
                key={item.label}
                className="rounded-xl border border-gray-100 bg-gray-50/60 p-3 dark:border-white/5 dark:bg-white/[0.02]"
              >
                <p className="line-clamp-2 min-h-[2.25rem] text-[11px] text-gray-500 dark:text-gray-400 sm:text-xs">
                  {item.label}
                </p>
                {loading ? (
                  <Skeleton className="mt-2 h-6 w-12" />
                ) : (
                  <p className="mt-1 text-lg font-bold text-navy-700 dark:text-white sm:text-xl">
                    {item.value}
                  </p>
                )}
              </div>
            ))}
          </div>
        </DashboardSection>
      </div>

      <DashboardSection
        title={isArabic ? "إجراءات سريعة" : "Quick actions"}
        subtitle={isArabic ? "اختصارات للمهام الشائعة" : "Shortcuts to common tasks"}
      >
        <div className={UI_ACTIONS_GRID}>
          {quickActions.map((action) => (
            <Link
              key={action.href + action.label}
              to={action.href}
              className="apple-action-tile flex min-h-[52px] items-center gap-3 px-3 py-3 text-sm font-semibold sm:px-4"
            >
              <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-lightPrimary text-brand-500 dark:bg-navy-700 dark:text-white">
                {action.icon}
              </span>
              <span className="flex-1 truncate">{action.label}</span>
              <MdAdd className="h-4 w-4 shrink-0 opacity-60" />
            </Link>
          ))}
        </div>
      </DashboardSection>

      <DashboardSection
        title={isArabic ? "أحدث البيانات" : "Recent activity"}
        subtitle={isArabic ? "آخر السجلات المضافة في النظام" : "Latest records added to the system"}
        action={
          <span className="inline-flex items-center gap-1 text-[11px] text-gray-400 dark:text-gray-500 sm:text-xs">
            <MdTrendingUp className="h-4 w-4" />
            {isArabic ? "قراءة فقط" : "Read-only"}
          </span>
        }
      >
        <div className="grid grid-cols-1 gap-4 sm:gap-5 xl:grid-cols-2">
          <div>
            <h3 className="mb-3 text-sm font-bold text-navy-700 dark:text-white">
              {isArabic ? "أحدث الطلاب" : "Latest students"}
            </h3>
            <RecentList<DashboardRecentStudent>
              loading={loading}
              items={recent?.students ?? []}
              emptyLabel={isArabic ? "لا يوجد طلاب بعد." : "No students yet."}
              renderItem={(student) => (
                <Link
                  to="/admin/students"
                  className="flex items-center justify-between gap-3 py-3 text-sm hover:text-brand-600 dark:hover:text-brand-300"
                >
                  <span className="min-w-0 flex-1 truncate font-medium text-gray-800 dark:text-white">
                    {student.name}
                  </span>
                  <span className="shrink-0 whitespace-nowrap text-[11px] text-gray-400 sm:text-xs">
                    {formatDashboardDate(student.created_at, locale)}
                  </span>
                </Link>
              )}
            />
          </div>

          <div>
            <h3 className="mb-3 text-sm font-bold text-navy-700 dark:text-white">
              {isArabic ? "أحدث المدرسين" : "Latest instructors"}
            </h3>
            <RecentList<DashboardRecentInstructor>
              loading={loading}
              items={recent?.instructors ?? []}
              emptyLabel={isArabic ? "لا يوجد مدرسون بعد." : "No instructors yet."}
              renderItem={(instructor) => (
                <Link
                  to="/admin/instructors"
                  className="flex items-center justify-between gap-3 py-3 text-sm hover:text-brand-600 dark:hover:text-brand-300"
                >
                  <span className="min-w-0 flex-1 truncate font-medium text-gray-800 dark:text-white">
                    {instructor.name}
                  </span>
                  <span className="shrink-0 whitespace-nowrap text-[11px] text-gray-400 sm:text-xs">
                    {formatDashboardDate(instructor.created_at, locale)}
                  </span>
                </Link>
              )}
            />
          </div>

          <div>
            <h3 className="mb-3 text-sm font-bold text-navy-700 dark:text-white">
              {isArabic ? "أحدث الكورسات" : "Latest courses"}
            </h3>
            <RecentList<DashboardRecentCourse>
              loading={loading}
              items={recent?.courses ?? []}
              emptyLabel={isArabic ? "لا توجد كورسات بعد." : "No courses yet."}
              renderItem={(course) => (
                <Link
                  to="/admin/entities"
                  className="flex items-center justify-between gap-3 py-3 text-sm hover:text-brand-600 dark:hover:text-brand-300"
                >
                  <span className="min-w-0 flex-1 truncate font-medium text-gray-800 dark:text-white">
                    {course.title}
                  </span>
                  <span className="shrink-0 whitespace-nowrap text-[11px] text-gray-400 sm:text-xs">
                    {formatDashboardDate(course.created_at, locale)}
                  </span>
                </Link>
              )}
            />
          </div>

          <div>
            <h3 className="mb-3 text-sm font-bold text-navy-700 dark:text-white">
              {isArabic ? "أحدث التسجيلات" : "Latest enrollments"}
            </h3>
            <RecentList<DashboardRecentEnrollment>
              loading={loading}
              items={recent?.enrollments ?? []}
              emptyLabel={isArabic ? "لا توجد تسجيلات بعد." : "No enrollments yet."}
              renderItem={(enrollment) => (
                <Link
                  to="/admin/entities"
                  className="flex flex-col gap-1 py-3 text-sm hover:text-brand-600 dark:hover:text-brand-300 sm:flex-row sm:items-center sm:justify-between sm:gap-3"
                >
                  <span className="min-w-0 flex-1 truncate font-medium text-gray-800 dark:text-white">
                    {enrollment.student_name} → {enrollment.course_title}
                  </span>
                  <span className="shrink-0 whitespace-nowrap text-[11px] text-gray-400 sm:text-xs">
                    {formatDashboardDate(enrollment.created_at, locale)}
                  </span>
                </Link>
              )}
            />
          </div>

        </div>
      </DashboardSection>
    </div>
  );
};

export default Dashboard;
